import axios from 'axios';
import type {
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosResponse,
} from 'axios';

/**
 * Conditional HTTPS agent for Node.js (Server-side only).
 */
async function getHttpsAgent(url: string) {
  // Browser check
  if (typeof (globalThis as any).window !== 'undefined') return undefined;

  try {
    const { Agent } = await import('https');
    const isLocal = url.includes('.local') || url.includes('localhost');

    // Only allow insecure connections if it's a local domain OR if explicitly forced in dev
    const shouldSkipValidation =
      isLocal || process.env.NODE_ENV === 'development';

    return new Agent({
      rejectUnauthorized: !shouldSkipValidation,
    });
  } catch (e) {
    return undefined;
  }
}

// Define the base URL
const URL: string =
  process.env.NUXT_SERVER_WP_API_URL || `https://trueislam.local/wp-json/`;

// Store the agent promise
const httpsAgentPromise = getHttpsAgent(URL);

// Define the cache object type
interface CacheEntry {
  data: AxiosResponse;
  timestamp: number;
}

// Define the cache structure
const cache: Record<string, CacheEntry> = {};

const CACHE_DURATION = 60000; // Cache duration in milliseconds (e.g., 60 seconds)

// Function to generate a unique cache key
const generateCacheKey = (config: InternalAxiosRequestConfig): string => {
  const { url, params } = config;
  const serializedParams = params ? JSON.stringify(params) : '';
  return `${url}?${serializedParams}`;
};

// Create Axios instance
const wpAPI: AxiosInstance = axios.create({
  baseURL: URL,
  timeout: 30000,
  transformResponse: [
    function (data: string) {
      try {
        return JSON.parse(data);
      } catch (error) {
        if (typeof data === 'string' && data.startsWith('<!DOCTYPE')) {
          return null;
        }
        return data;
      }
    },
  ],
});

// Add a request interceptor to check the cache and attach HTTPS agent
wpAPI.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Attach the httpsAgent for server-side requests
    if (typeof (globalThis as any).window === 'undefined') {
      config.httpsAgent = await httpsAgentPromise;
    }

    // Check if we're in development mode
    const isDevelopment = process.env.NODE_ENV === 'development';

    if (!isDevelopment && config.url) {
      const cacheKey = generateCacheKey(config);
      const cachedResponse = cache[cacheKey];

      if (
        cachedResponse &&
        Date.now() - cachedResponse.timestamp < CACHE_DURATION
      ) {
        // Reject the request and return cached data
        return Promise.reject({ __fromCache: true, data: cachedResponse.data });
      }
    }

    return config;
  },
  (error: any) => {
    return Promise.reject(error);
  },
);

// Add a response interceptor to cache the response
wpAPI.interceptors.response.use(
  (response: AxiosResponse) => {
    if (response.config.url) {
      const cacheKey = generateCacheKey(response.config);
      // Cache the response data along with the timestamp
      cache[cacheKey] = {
        data: response,
        timestamp: Date.now(),
      };
    }
    return response;
  },
  (error: any) => {
    if (error.__fromCache) {
      // Return the cached data if the request was served from the cache
      return Promise.resolve(error.data);
    }
    // Reject the error if it wasn't cached
    return Promise.reject(error);
  },
);

export default wpAPI;
